/**
 * Shared layout for transcript views: timeline swimlanes, transcript outline,
 * and the virtualized event list. Apps provide events, selection state
 * adapters, and store-backed collapse callbacks.
 */

import clsx from "clsx";
import {
  CSSProperties,
  FC,
  ReactNode,
  RefObject,
  useCallback,
  useMemo,
  useRef,
} from "react";

import type {
  Event,
  Timeline as ServerTimeline,
} from "@tsmono/inspect-common/types";
import {
  NoContentsPanel,
  RailDock,
  StickyScroll,
} from "@tsmono/react/components";
import { useElementHeight } from "@tsmono/react/hooks";

import type { RowSelection } from "../selection/rowSelection";

import { useDeepLinkResolution } from "./hooks/useDeepLinkResolution";
import { useEventNodeData } from "./hooks/useEventNodeData";
import { deriveFocusLanes, type FocusLane } from "./hooks/useFocusLaneScope";
import { useListPositionManager } from "./hooks/useListPositionManager";
import { useOutlineAutoHide } from "./hooks/useOutlineAutoHide";
import { useSelectionActions } from "./hooks/useSelectionActions";
import { useSidebarScrollCoupling } from "./hooks/useSidebarScrollCoupling";
import { useStickySwimLaneHeight } from "./hooks/useStickySwimLaneHeight";
import { useSwimlaneHeader } from "./hooks/useSwimlaneHeader";
import { useTimelinePipeline } from "./hooks/useTimelinePipeline";
import { useTranscriptCollapse } from "./hooks/useTranscriptCollapse";
import {
  OutlineSidebar,
  type TranscriptLayoutOutlineProps,
} from "./OutlineSidebar";
import { computeLaneFirstAnchors } from "./resolveMessageToEvent";
import { useTranscriptSearchSource } from "./search";
import { AgentCardView, TimelineSwimLanes } from "./timeline/components";
import { countUtilitySpans, TimelineSpan } from "./timeline/core";
import {
  type SelectOptions,
  type TimelineOptions,
  type UseActiveTimelineProps,
  type UseTimelineProps,
} from "./timeline/hooks";
import { type MarkerConfig } from "./timeline/markers";
import { getAgents } from "./timeline/swimlaneRows";
import {
  TimelineRowSelectContext,
  TimelineSelectContext,
} from "./TimelineSelectContext";
import styles from "./TranscriptLayout.module.css";
import {
  TranscriptViewNodes,
  type TranscriptViewNodesHandle,
} from "./TranscriptViewNodes";
import {
  EventNode,
  type EventNodeContext,
  type TranscriptCollapseState,
} from "./types";

// =============================================================================
// Types
// =============================================================================

export { type TranscriptLayoutOutlineProps } from "./OutlineSidebar";

export interface TranscriptLayoutRightRailProps {
  /** Always-visible rail content (the vertical activity bar). */
  rail: ReactNode;
  /** Panel content shown in a column to the LEFT of the rail. Null = no panel open. */
  panel?: ReactNode;
  /** Fixed rail width in px. Defaults to 44. */
  railWidth?: number;
  /** Controlled panel width (px). Pair with onPanelWidthChange so dragging
   *  still applies; omit both for panel-local width (360 default). */
  panelWidth?: number;
  /** Called with the new width as the user drags the panel's resize handle. */
  onPanelWidthChange?: (width: number) => void;
  panelMinWidth?: number;
  panelMaxWidth?: number;
  /** aria-label root for the panel region. */
  label?: string;
}

/** Timeline data, selection adapters, and swimlane behavior
 *  (consumed by the timeline pipeline + swimlane header). */
export interface TranscriptLayoutTimelineProps {
  /** Row selection state adapter. */
  selection?: UseTimelineProps;
  /** Active-timeline adapter (multi-timeline logs). */
  active?: UseActiveTimelineProps;
  serverTimelines?: ServerTimeline[];
  /** Marker config override (default: useTimelineConfig()). */
  markerConfig?: MarkerConfig;
  /** Agent timeline options override (default: useTimelineConfig()). */
  agentConfig?: TimelineOptions;
  /** Swimlane visibility. "auto" (the default) shows them when the timeline
   *  has agent structure. */
  showSwimlanes?: boolean | "auto";
  onMarkerNavigate?: (eventId: string, selectedKey?: string) => void;
  /** Called on swimlane header click to scroll the view to the top. */
  onScrollToTop?: () => void;
}

/** Deep-link target resolved on mount (consumed by useDeepLinkResolution). */
export interface TranscriptLayoutDeepLinkProps {
  /** Deep-link to a specific event. Takes priority over messageId. */
  eventId?: string | null;
  /** Deep-link to a message ID, resolved to the best matching event.
   *  Used for citation navigation — resolves against selected span first, then root. */
  messageId?: string | null;
  /** Explicit `follow=1` URL param: arm live-tail at mount even though the
   *  event/message deep link also makes this a nav-owned mount. */
  follow?: boolean;
}

/** Adapter for the host's headroom (collapsing chrome above the transcript). */
export interface TranscriptLayoutHeadroomProps {
  hidden?: boolean;
  /** Force the headroom into the given hidden state. Used by sources (e.g.
   *  search) that drive scroll-direction-sensitive UI when their motion
   *  doesn't match what the scroll-direction tracker would infer. */
  onSetHidden?: (hidden: boolean) => void;
  onResetAnchor?: (debounce?: boolean) => void;
}

/** Empty-state display when no events match the current filter. */
export interface TranscriptLayoutEmptyProps {
  /** Text shown when no events match. Pass null to disable the empty state. */
  text?: string | null;
  /** Render the empty state as an in-progress placeholder (animated, no icon). */
  busy?: boolean;
}

export interface TranscriptLayoutProps {
  // --- Events ---
  events: Event[];
  /** Event types to hide from the rendered card list. Applied after timeline
   *  construction so structural events (anchor/branch) still resolve. */
  hiddenEventTypes?: readonly string[];
  /** Whether events are still being streamed (enables running indicators). */
  running?: boolean;
  /** Whether the sample's event backlog is still loading (live sample). */
  backfilling?: boolean;
  /** Whether a live→finished transition may scroll the view to the top —
   *  false for unsuccessful finishes (error/cancelled). */
  scrollToTopOnFinish?: boolean;

  // --- Scroll & positioning ---
  scrollRef: RefObject<HTMLDivElement | null>;
  offsetTop?: number;
  /** Size the layout to its content instead of filling the viewport. Use when
   *  embedding the transcript inside another scroll container (e.g. a card)
   *  rather than as the page's primary scroll region. */
  embedded?: boolean;

  // --- Feature groups ---
  /** Timeline data, selection adapters, and swimlane behavior. */
  timeline?: TranscriptLayoutTimelineProps;
  /** Deep-link target resolved on mount. */
  deepLink?: TranscriptLayoutDeepLinkProps;
  /** Host headroom adapter. */
  headroom?: TranscriptLayoutHeadroomProps;
  /** Empty-state display. Omit for the default text. */
  empty?: TranscriptLayoutEmptyProps;

  // --- Event list ---
  listId: string;
  eventsListRef?: RefObject<TranscriptViewNodesHandle | null>;
  getEventUrl?: (eventId: string) => string | undefined;
  linkingEnabled?: boolean;
  /** Builds the focus-mode entry href for the header's focus control
   *  (plain click enters in-window; modified clicks open a new tab). Omit to
   *  hide that control. */
  getEventFocusUrl?: (
    eventId: string,
    selectedTab?: string
  ) => string | undefined;
  /** Navigate to a focus URL in the current window. */
  onOpenEventFocus?: (focusRoute: string) => void;
  /** Reflect an explicit turn navigation (j/k, chevrons, editable number) in
   *  the URL (`?event=`, replace) — like an outline click. Not called on scroll. */
  onNavigatedToEvent?: (eventId: string) => void;
  /** Disable transcript keyboard nav (j/k/h/l/gg/G) while find-in-page owns the
   *  keyboard, so its keys reach the find box instead of navigating turns. */
  keyboardNavDisabled?: boolean;
  /** Evidence selection (host-owned state). Pass it only while selection mode
   *  is on: its presence shows the per-event header checkboxes. */
  selection?: RowSelection;

  // --- Collapse state (from app store) ---
  /** Bulk collapse/expand of all collapsible events. Omit for no-op. */
  bulkCollapse?: "collapse" | "expand";
  collapseState?: TranscriptCollapseState;

  // --- Outline ---
  /** Outline panel configuration. When omitted, the outline column is hidden entirely. */
  outline?: TranscriptLayoutOutlineProps;

  // --- Right rail ---
  /** Optional always-visible right rail + optional panel. */
  rightRail?: TranscriptLayoutRightRailProps;

  /** Extra context fields merged into every EventNodeContext entry. */
  eventNodeContext?: Partial<EventNodeContext>;
  className?: string;
}

// =============================================================================
// Component
// =============================================================================

/**
 * CSS custom properties for a `style` prop. csstype's `Properties` declares no
 * `--*` index signature, so a custom-property literal can't be written inline;
 * React passes these straight through to the DOM.
 */
const cssVars = (vars: Record<`--${string}`, string | number>): CSSProperties =>
  vars;

function renderAgentCard(node: EventNode, agentCardClassName?: string) {
  const span = node.sourceSpan;
  if (!(span instanceof TimelineSpan)) return null;
  return <AgentCardView span={span} className={agentCardClassName} />;
}

export const TranscriptLayout: FC<TranscriptLayoutProps> = ({
  events,
  hiddenEventTypes,
  running = false,
  backfilling = false,
  scrollToTopOnFinish,
  scrollRef,
  offsetTop = 0,
  embedded = false,
  timeline,
  deepLink,
  headroom,
  empty,
  listId,
  eventsListRef,
  getEventUrl,
  linkingEnabled,
  getEventFocusUrl,
  onOpenEventFocus,
  onNavigatedToEvent,
  keyboardNavDisabled,
  selection,
  bulkCollapse,
  collapseState,
  outline,
  rightRail,
  eventNodeContext,
  className,
}) => {
  // Group props destructure to locals immediately: the group objects are
  // typically fresh literals at call sites, but the values inside keep the
  // caller's identity — hooks below depend on the values, never the groups.
  const {
    selection: timelineSelection,
    active: activeTimeline,
    serverTimelines,
    markerConfig: markerConfigOverride,
    agentConfig: agentConfigOverride,
    showSwimlanes: showSwimlanesOption = "auto",
    onMarkerNavigate,
    onScrollToTop,
  } = timeline ?? {};
  const {
    eventId: initialEventId,
    messageId: initialMessageId,
    follow: initialFollowRequested,
  } = deepLink ?? {};
  const {
    hidden: headroomHidden,
    onSetHidden: onHeadroomSetHidden,
    onResetAnchor: onHeadroomResetAnchor,
  } = headroom ?? {};
  const {
    text: emptyText = "No events match the current filter",
    busy: emptyBusy,
  } = empty ?? {};
  // ---------------------------------------------------------------------------
  // Timeline pipeline + event nodes
  // ---------------------------------------------------------------------------

  const {
    timeline: transcriptTimeline,
    timelineConfig,
    showSwimlanes,
    swimlanesDefaultCollapsed,
    nodeFeed,
    searchableEvents,
  } = useTimelinePipeline({
    events,
    hiddenEventTypes,
    serverTimelines,
    markerConfig: markerConfigOverride,
    agentConfig: agentConfigOverride,
    showSwimlanes: showSwimlanesOption,
    timelineSelection,
    activeTimeline,
  });

  const {
    timeline: timelineData,
    state: timelineState,
    swimlanes: { layouts: timelineLayouts, regionCounts, highlightedKeys },
    minimap,
    multiTimeline,
    views,
    selection: { rowName: selectedRowName },
  } = transcriptTimeline;
  const { timelines, activeIndex: activeTimelineIndex } = multiTimeline;

  const {
    eventNodes,
    defaultCollapsedIds,
    eventNodeContext: mergedEventNodeContext,
  } = useEventNodeData(nodeFeed, running, eventNodeContext, events);

  const nullViewNodesRef = useRef<TranscriptViewNodesHandle | null>(null);

  useTranscriptSearchSource({
    id: listId,
    events: searchableEvents,
    rows: timelineState.rows,
    selected: timelineSelection?.selected ?? null,
    onSelect: timelineSelection?.onSelect ?? (() => {}),
    viewNodesRef: eventsListRef ?? nullViewNodesRef,
    onHeadroomResetAnchor,
    onHeadroomSetHidden,
  });

  // ---------------------------------------------------------------------------
  // Sticky swimlane height
  // ---------------------------------------------------------------------------

  const {
    stickySwimLaneHeight,
    isSwimLaneSticky,
    swimLaneStickyContentRef,
    handleSwimLaneStickyChange,
  } = useStickySwimLaneHeight();

  const effectiveOffsetTop = offsetTop + stickySwimLaneHeight;

  // ---------------------------------------------------------------------------
  // Selection actions + per-agent list position management
  // ---------------------------------------------------------------------------

  const {
    spanSelectKeys,
    selectByRowKey: baseSelectByRowKey,
    hasScrollTarget,
  } = useSelectionActions({
    timelineState,
    scrollRef,
    initialEventId,
    initialMessageId,
  });

  const { effectiveListId } = useListPositionManager(
    listId,
    timelineState.selected,
    scrollRef,
    hasScrollTarget
  );

  // ---------------------------------------------------------------------------
  // Swimlane header
  // ---------------------------------------------------------------------------

  const hiddenUtilityCount = useMemo(
    () => countUtilitySpans(transcriptTimeline.timeline.root),
    [transcriptTimeline.timeline.root]
  );

  // Only agent lanes (root + subagents) are h/l-navigable — skip scoring/tool
  // rows so lane stepping matches the focus page, which is agent-only.
  const agentLaneKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const row of timelineState.rows) {
      if (
        row.depth === 0 ||
        row.spans.some((s) => getAgents(s).some((a) => a.spanType === "agent"))
      ) {
        keys.add(row.key);
      }
    }
    return keys;
  }, [timelineState.rows]);
  const laneKeys = useMemo(
    () =>
      timelineLayouts.filter((l) => agentLaneKeys.has(l.key)).map((l) => l.key),
    [timelineLayouts, agentLaneKeys]
  );

  // First turn-anchor per agent lane: a lane switch parks `?event=` on the
  // lane's first turn so the lane never lives only in selection state.
  const laneFirstAnchor = useMemo(() => {
    const byAgentSpan = computeLaneFirstAnchors(timelineData.root);
    const byLaneKey = new Map<string, string>();
    const rootKey = laneKeys[0];
    for (const [agentSpanId, eventId] of byAgentSpan) {
      const laneKey =
        agentSpanId === null ? rootKey : spanSelectKeys.get(agentSpanId)?.key;
      if (laneKey) byLaneKey.set(laneKey, eventId);
    }
    return byLaneKey;
  }, [timelineData.root, spanSelectKeys, laneKeys]);

  // Switch to an agent lane (by swimlane row key), parking `?event=` on its
  // first turn so the lane stays derivable from the URL (like j/k). Shared by
  // h/l, the swimlane row clicks, and the in-stream agent cards.
  const selectLaneByKey = useCallback(
    (laneKey: string) => {
      // Skip only when this lane is genuinely the active one. A non-lane
      // selection (branch/scoring) maps to currentLaneIndex 0 but is NOT "on
      // main", so clicking main from there must still clear + park.
      const current = timelineState.selected;
      const alreadyActive =
        laneKey === laneKeys[0]
          ? current === null || current === laneKeys[0]
          : current === laneKey;
      if (alreadyActive) return;
      onHeadroomResetAnchor?.(true); // don't flicker headroom during the scroll
      const targetEvent = laneFirstAnchor.get(laneKey);
      if (laneKey === laneKeys[0]) {
        timelineState.clearSelection();
      } else {
        // No anchor (branch/multi-span lane): don't preserve, else the previous
        // lane's stale `?event=` survives and mislabels the lane.
        timelineState.select(laneKey, {
          preserveDeepLink: targetEvent !== undefined,
        });
      }
      if (targetEvent) onNavigatedToEvent?.(targetEvent);
    },
    [
      laneKeys,
      onHeadroomResetAnchor,
      laneFirstAnchor,
      timelineState,
      onNavigatedToEvent,
    ]
  );
  // select() for the swimlane + agent cards: agent-lane keys park the URL,
  // span/region/non-agent keys fall through to plain selection.
  const laneAwareSelect = useCallback(
    (key: string | null, options?: SelectOptions) => {
      if (key !== null && laneKeys.includes(key)) {
        selectLaneByKey(key);
      } else {
        timelineState.select(key, options);
      }
    },
    [laneKeys, selectLaneByKey, timelineState]
  );
  const swimlaneNav = useMemo(
    () => ({
      node: timelineState.node,
      selected: timelineState.selected,
      select: laneAwareSelect,
      clearSelection: timelineState.clearSelection,
    }),
    [
      timelineState.node,
      timelineState.selected,
      timelineState.clearSelection,
      laneAwareSelect,
    ]
  );
  // Cross-timeline lane list shared with the focus page (deriveFocusLanes),
  // so both views navigate lanes — including across root timelines — identically.
  const navLanes = useMemo(
    () =>
      deriveFocusLanes(
        timelineState.rows,
        timelineData.root,
        timelines,
        activeTimelineIndex,
        timelineState.selected
      ),
    [
      timelineState.rows,
      timelineData.root,
      timelines,
      activeTimelineIndex,
      timelineState.selected,
    ]
  );
  // Within the active timeline, select the swimlane row (existing path). A lane
  // in another root timeline is reached by parking `?event=` on its first
  // anchor — the deep-link effect then switches the active timeline.
  const goToNavLane = useCallback(
    (lane: FocusLane | undefined) => {
      if (!lane) return;
      if (lane.timelineIndex === activeTimelineIndex && lane.laneKey) {
        selectLaneByKey(lane.laneKey);
      } else if (lane.firstAnchorId) {
        onHeadroomResetAnchor?.(true);
        onNavigatedToEvent?.(lane.firstAnchorId);
      }
    },
    [
      activeTimelineIndex,
      selectLaneByKey,
      onHeadroomResetAnchor,
      onNavigatedToEvent,
    ]
  );
  const adjacentNavLane = useCallback(
    (delta: 1 | -1): FocusLane | undefined => {
      for (
        let i = navLanes.laneIndex + delta;
        i >= 0 && i < navLanes.lanes.length;
        i += delta
      ) {
        const lane = navLanes.lanes[i];
        if (
          lane?.firstAnchorId ||
          (lane?.timelineIndex === activeTimelineIndex && lane.laneKey)
        ) {
          return lane;
        }
      }
      return undefined;
    },
    [activeTimelineIndex, navLanes]
  );
  const prevNavLane = adjacentNavLane(-1);
  const nextNavLane = adjacentNavLane(1);
  const onLanePrev = useCallback(
    () => goToNavLane(prevNavLane),
    [goToNavLane, prevNavLane]
  );
  const onLaneNext = useCallback(
    () => goToNavLane(nextNavLane),
    [goToNavLane, nextNavLane]
  );
  // Lane-aware wrappers over the selection actions: agent-lane keys park the
  // URL via selectLaneByKey; non-lane keys fall through to the base actions.
  const selectBySpanId = useCallback(
    (spanId: string) => {
      const key = spanSelectKeys.get(spanId);
      if (key) laneAwareSelect(key.key);
    },
    [spanSelectKeys, laneAwareSelect]
  );

  const selectByRowKey = useCallback(
    (rowKey: string, anchorEl?: HTMLElement) => {
      if (laneKeys.includes(rowKey)) {
        selectLaneByKey(rowKey);
        return;
      }
      // Non-lane rows scope without parking the URL, keeping scroll position.
      baseSelectByRowKey(rowKey, anchorEl);
    },
    [laneKeys, selectLaneByKey, baseSelectByRowKey]
  );

  const swimlaneHeader = useSwimlaneHeader({
    scrollRef,
    onScrollToTop,
    onHeadroomResetAnchor,
    timelineConfig,
    hiddenUtilityCount,
    minimap,
    multiTimeline,
    views,
  });

  // ---------------------------------------------------------------------------
  // Deep-link resolution
  // ---------------------------------------------------------------------------

  const { effectiveInitialEventId } = useDeepLinkResolution({
    initialEventId,
    initialMessageId,
    timeline: transcriptTimeline,
    spanSelectKeys,
    showSwimlanes,
    nodeFeedEvents: nodeFeed.events,
    onHeadroomResetAnchor,
    includeUtility: timelineConfig.includeUtility,
    setIncludeUtility: timelineConfig.setIncludeUtility,
  });

  // ---------------------------------------------------------------------------
  // Collapse state & outline auto-hide
  // ---------------------------------------------------------------------------

  const { onCollapseTranscript, onExpandNodes } = useTranscriptCollapse({
    eventNodes,
    defaultCollapsedIds,
    collapseState,
    bulkCollapse,
    eventCount: events.length,
  });

  const { isOutlineCollapsed, outlineHasNodes, onOutlineHasNodesChange } =
    useOutlineAutoHide({
      eventNodes,
      hasOutline: !!outline,
      outlineCollapsed: outline?.collapsed,
    });

  const hasMatchingEvents = eventNodes.length > 0;

  // ---------------------------------------------------------------------------
  // Headroom reset anchor
  // ---------------------------------------------------------------------------

  const handleLayoutShift = useCallback(
    (debounce?: boolean) => onHeadroomResetAnchor?.(debounce),
    [onHeadroomResetAnchor]
  );

  // The rail panel's scroll container is written by RailDock and read only by
  // the wheel coupling below — no caller sees it, so the layout owns the ref.
  const railPanelScrollRef = useRef<HTMLDivElement | null>(null);

  // Rail and outline pin at different offsets, so each needs its own sticky
  // threshold; the remount keys re-attach listeners across conditional renders.
  useSidebarScrollCoupling({
    mainScrollRef: scrollRef,
    sidebars: [
      {
        scrollRef: outline?.scrollRef,
        stickyTop: effectiveOffsetTop,
        remountKey: outline?.collapsed ?? null,
      },
      {
        scrollRef: railPanelScrollRef,
        stickyTop: offsetTop,
        remountKey: rightRail?.panel != null,
      },
    ],
  });

  // Track the scroll container's visible height so sticky sidebars can cap
  // their max-height to the actually-visible area (100vh would include the
  // app navbar above the scroll container, leaving content unreachable).
  const scrollerHeight = useElementHeight(scrollRef);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <TimelineSelectContext.Provider value={selectBySpanId}>
      <TimelineRowSelectContext.Provider value={selectByRowKey}>
        <div className={clsx(styles.root, className)}>
          <div className={styles.main}>
            {showSwimlanes && (
              <StickyScroll
                scrollRef={scrollRef}
                offsetTop={offsetTop}
                zIndex={500}
                preserveHeight={true}
                onStickyChange={handleSwimLaneStickyChange}
              >
                <div ref={swimLaneStickyContentRef}>
                  <TimelineSwimLanes
                    layouts={timelineLayouts}
                    timeline={swimlaneNav}
                    header={swimlaneHeader}
                    onMarkerNavigate={onMarkerNavigate}
                    isSticky={isSwimLaneSticky}
                    // NOT gated on stickiness: StickyScroll's first report is
                    // async, which made the initial state timing-dependent.
                    headroomCollapsed={!!headroomHidden}
                    onLayoutShift={handleLayoutShift}
                    defaultCollapsed={swimlanesDefaultCollapsed}
                    regionCounts={regionCounts}
                    highlightedKeys={highlightedKeys}
                    onPunchDown={views.pushByRowKey}
                  />
                </div>
              </StickyScroll>
            )}
            <div
              className={clsx(
                styles.container,
                embedded && styles.embedded,
                !outline && styles.noOutline,
                outline && isOutlineCollapsed && styles.outlineCollapsed
              )}
              style={cssVars({
                "--outline-top": `${effectiveOffsetTop}px`,
                "--scroller-height": scrollerHeight
                  ? `${scrollerHeight}px`
                  : "100vh",
              })}
            >
              {outline && (
                <OutlineSidebar
                  outline={outline}
                  isCollapsed={isOutlineCollapsed}
                  hasNodes={outlineHasNodes}
                  onHasNodesChange={onOutlineHasNodesChange}
                  listId={listId}
                  eventNodes={eventNodes}
                  defaultCollapsedIds={defaultCollapsedIds}
                  scrollRef={scrollRef}
                  running={running}
                  backfilling={backfilling}
                  agentName={
                    outline.name ??
                    (showSwimlanes ? selectedRowName : undefined)
                  }
                  offsetTop={effectiveOffsetTop}
                  collapseState={collapseState}
                  getEventUrl={getEventUrl}
                />
              )}
              {hasMatchingEvents ? (
                <TranscriptViewNodes
                  key={effectiveListId}
                  ref={eventsListRef}
                  id={effectiveListId}
                  eventNodes={eventNodes}
                  defaultCollapsedIds={defaultCollapsedIds}
                  running={running}
                  backfilling={backfilling}
                  scrollToTopOnFinish={scrollToTopOnFinish}
                  initialEventId={effectiveInitialEventId}
                  initialMessageId={initialMessageId}
                  followRequested={initialFollowRequested}
                  offsetTop={effectiveOffsetTop}
                  className={styles.eventsList}
                  scrollRef={scrollRef}
                  renderAgentCard={showSwimlanes ? renderAgentCard : undefined}
                  getEventUrl={getEventUrl}
                  linkingEnabled={linkingEnabled}
                  getEventFocusUrl={getEventFocusUrl}
                  onOpenEventFocus={onOpenEventFocus}
                  collapsedTranscript={collapseState?.transcript}
                  onCollapseTranscript={onCollapseTranscript}
                  onExpandNodes={onExpandNodes}
                  eventNodeContext={mergedEventNodeContext}
                  onProgrammaticScroll={onHeadroomResetAnchor}
                  onHeadroomSetHidden={onHeadroomSetHidden}
                  // Wired only with real lanes so h/l aren't swallowed for a
                  // no-op on single-lane transcripts.
                  onPrevAgent={
                    navLanes.lanes.length > 1 ? onLanePrev : undefined
                  }
                  onNextAgent={
                    navLanes.lanes.length > 1 ? onLaneNext : undefined
                  }
                  onNavigatedToEvent={onNavigatedToEvent}
                  keyboardNavDisabled={keyboardNavDisabled}
                  selection={selection}
                />
              ) : emptyText !== null ? (
                <NoContentsPanel text={emptyText} busy={emptyBusy} />
              ) : null}
            </div>
          </div>
          {/* The rail (and its optional resizable panel) render as a flex
              region to the right of the timeline + content, so they run full
              height starting directly below the toolbar rather than below the
              swimlanes. */}
          {rightRail && (
            <RailDock
              rail={rightRail.rail}
              panel={rightRail.panel}
              scrollRef={scrollRef}
              scrollerHeight={scrollerHeight}
              offsetTop={offsetTop}
              panelScrollRef={railPanelScrollRef}
              railWidth={rightRail.railWidth}
              panelWidth={rightRail.panelWidth}
              onPanelWidthChange={rightRail.onPanelWidthChange}
              panelMinWidth={rightRail.panelMinWidth}
              panelMaxWidth={rightRail.panelMaxWidth}
              label={rightRail.label}
            />
          )}
        </div>
      </TimelineRowSelectContext.Provider>
    </TimelineSelectContext.Provider>
  );
};
